<template>
  <div class="h-14 sm:h-16 w-full bg-gradient-to-r from-gray-800 to-gray-700 flex items-center shadow-lg z-40 text-xs sm:text-sm">
    <!-- Left Section: Logo & Navigation -->
    <div class="flex items-center gap-2 sm:gap-6 px-2 sm:px-6">
      <!-- App Branding -->
      <div class="flex flex-col">
        <div class="flex items-baseline">
          <span class="text-white text-xl sm:text-2xl font-semibold">PAM<sup class="text-[10px] sm:text-sm">v2</sup></span>
          <span class="ml-2 text-orange-400 text-[11px] font-medium hidden sm:inline">by HMGBS</span>
        </div>
        <span class="text-gray-300 text-[10px] tracking-wide hidden lg:block">Programmatic-Abrechnungsmanager</span>
      </div>

      <!-- Divider -->
      <div class="h-10 w-px bg-gray-600 hidden sm:block"></div>

      <!-- Navigation Controls Group -->
      <div class="flex items-center gap-1 sm:gap-2">
        <!-- Show "Jahresübersicht" button when NOT in overview page -->
        <button v-if="!isOverviewPage" @click="goToAnnualOverview" class="hidden sm:inline-flex items-center gap-2 rounded-sm bg-black bg-opacity-20 px-4 py-2.5 font-medium text-white hover:bg-opacity-30 focus:outline-none transition-all">
          <font-awesome-icon icon="fa-table-cells" />
          <span>Jahresübersicht</span>
        </button>
        <!-- Show "Zurück" button when IN overview page -->
        <button v-else @click="goBackToLatestMonth" class="hidden sm:inline-flex items-center gap-2 rounded-sm bg-black bg-opacity-20 px-4 py-2.5 font-medium text-white hover:bg-opacity-30 focus:outline-none transition-all">
          <font-awesome-icon icon="fa-chevron-left" />
          <span>Zurück</span>
        </button>
        <DropDownView :label="selectedYear" :items="years" @valueSelected="selectYear" :fullWidthMobile="true"/>
        <!-- Hide month dropdown when in overview page -->
        <DropDownView v-if="!isOverviewPage" :label="api.monthNames[selectedMonth - 1]" :items="store.getMonths(selectedYear)" @valueSelected="selectMonth" :fullWidthMobile="true"/>
      </div>
    </div>

    <!-- Right Section: Refresh Button & User Info -->
    <div class="flex-grow" />
    <button
      v-if="!isOverviewPage"
      @click="triggerRefresh"
      class="mr-4 hidden sm:inline-flex items-center gap-2 rounded-sm bg-black bg-opacity-20 px-4 py-2.5 font-medium text-white hover:bg-opacity-30 focus:outline-none transition-all"
      :disabled="isRefreshing"
    >
      <font-awesome-icon :icon="isRefreshing ? 'fa-circle-notch' : 'fa-rotate'" :class="{ 'animate-spin': isRefreshing }" />
      <span>{{ isRefreshing ? 'Aktualisiere...' : 'Aktualisieren' }}</span>
    </button>
    <UserInfo/>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import UserInfo from "@/components/reusable/UserInfo.vue"
import DropDownView from "@/components/reusable/DropDown.vue"
import api from "@/js/api"
import { useMainStore } from '@/stores/main'

const isRefreshing = ref(false)

const props = defineProps({
  selectedYear: String,
  selectedMonth: String,
  selectedSSP: String
})

// Router
const router = useRouter()
const route = useRoute()

// Store
const store = useMainStore()

// Computed
const isOverviewPage = computed(() => {
  return route.path.startsWith('/overview/')
})

const years = computed(() => {
  // Convert years from numbers to strings for the dropdown
  return store.getYears().map(year => String(year))
})

// Methods
const selectYear = (value) => {
  router.push("/" + value  + "/" + parseInt(props.selectedMonth) + "/" + encodeURIComponent(props.selectedSSP))
}

const selectMonth = (value) => {
  let monthNum = api.monthNames.indexOf(value)
  router.push("/" + props.selectedYear  + "/" + (monthNum + 1) + "/" + encodeURIComponent(props.selectedSSP))
}

const goToAnnualOverview = () => {
  router.push("/overview/" + props.selectedYear)
}

const goBackToLatestMonth = () => {
  // Navigate back in browser history
  router.go(-1)
}

const triggerRefresh = async () => {
  if (isRefreshing.value) return
  isRefreshing.value = true
  try {
    await api.refreshAll()
    store.showSuccessAlert("Datenaktualisierung wurde gestartet!")
  } catch (error) {
    store.showErrorAlert("Fehler beim Starten der Datenaktualisierung")
  } finally {
    isRefreshing.value = false
  }
}
</script>
